import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  SUPPORT_CATEGORIES,
  SUPPORT_CATEGORY_LABEL,
  getStatusLabel,
  type SupportCategory,
  type SupportTicketDto,
  type SupportTicketStatus,
} from '@shared';
import { apiGet, apiPost, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';

// Mirrors AdminLayout's ROLE_LABELS — kept inline so a single-use constant
// doesn't justify a new shared file. If a third portal page ever needs
// these labels, hoist to src/client/lib/role-labels.ts.
const ROLE_LABELS: Record<string, string> = {
  super: 'Super Admin', admin: 'Admin', lead: 'Engagement Lead',
  analyst: 'Analyst', compliance: 'Compliance', finance: 'Finance', readonly: 'Read-only',
};

// Chip tone by status — settled in Phase C and reused on both portal + admin
// for cross-portal consistency. open/awaiting_client → amber (action pending),
// acknowledged/in_progress → accent (in-flight), resolved → green, closed → neutral.
const STATUS_TONE: Record<SupportTicketStatus, string> = {
  open:            'amber',
  acknowledged:    'accent',
  in_progress:     'accent',
  awaiting_client: 'amber',
  resolved:        'green',
  closed:          'neutral',
};

interface FormState {
  category: SupportCategory;
  subject: string;
  body: string;
  related_ref: string;
}

const INITIAL: FormState = {
  category: 'engagement',
  subject: '',
  body: '',
  related_ref: '',
};

export function PortalSupport() {
  const { user } = useAuth();
  const [d, setD] = useState<FormState>(INITIAL);
  const [tickets, setTickets] = useState<SupportTicketDto[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedRef, setSubmittedRef] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ tickets: SupportTicketDto[] }>('/api/support')
      .then((r) => setTickets(r.tickets))
      .catch(() => setTickets([]));
  }, []);

  const set = <K extends keyof FormState>(k: K) => (e: { target: { value: string } }) =>
    setD((s) => ({ ...s, [k]: e.target.value as FormState[K] }));

  const ready = d.subject.trim().length >= 3 && d.body.trim().length >= 10;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        category: d.category,
        subject: d.subject.trim(),
        body: d.body.trim(),
        related_ref: d.related_ref.trim() || undefined,
      };
      const r = await apiPost<{ ticket: SupportTicketDto }>('/api/support', payload);
      setSubmittedRef(r.ticket.id);
      setD(INITIAL);
      setTickets((prev) => [r.ticket, ...(prev ?? [])]);
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Could not open ticket. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div>
        <h1 className="portal-h1">Support</h1>
        <p className="portal-sub">
          Stuck, confused, or need someone to look into your engagement? Open a ticket and a member
          of the Omega team will respond within 1 UK business day.
        </p>
      </div>

      {submittedRef && (
        <div className="card" style={{ marginTop: 20, borderColor: 'var(--green)', background: 'var(--green-soft, var(--bg-soft))' }}>
          <h3 style={{ fontSize: 16, margin: 0 }}>Ticket opened</h3>
          <p style={{ color: 'var(--fg-soft)', fontSize: 13.5, marginTop: 6, lineHeight: 1.6 }}>
            Reference <span className="rid">{submittedRef}</span>. We've also sent a confirmation
            to <span style={{ fontFamily: 'var(--mono)' }}>{user?.email}</span>.
          </p>
        </div>
      )}

      <form className="card" style={{ marginTop: 20, padding: 'clamp(20px, 3vw, 32px)' }} onSubmit={submit}>
        <h3 style={{ fontSize: 17, margin: 0 }}>Open a ticket</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 18 }}>
          <div className="field">
            <label>Category</label>
            <select value={d.category} onChange={set('category')} disabled={submitting}>
              {SUPPORT_CATEGORIES.map((c) => (
                <option key={c} value={c}>{SUPPORT_CATEGORY_LABEL[c]}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Related reference (optional)</label>
            <input
              value={d.related_ref}
              onChange={set('related_ref')}
              placeholder="e.g. INV-2049, OMG-2055"
              disabled={submitting}
            />
            <div className="hint">Quote a brief, proposal or invoice ID to scope the ticket.</div>
          </div>
        </div>

        <div className="field" style={{ marginTop: 14 }}>
          <label>Subject</label>
          <input
            value={d.subject}
            onChange={set('subject')}
            placeholder="Short summary of the issue"
            disabled={submitting}
            required
          />
        </div>

        <div className="field" style={{ marginTop: 14 }}>
          <label>Details</label>
          <textarea
            rows={6}
            value={d.body}
            onChange={set('body')}
            placeholder="What were you trying to do, what happened, and anything we should know."
            disabled={submitting}
            required
          />
          <div className="hint">Minimum 10 characters. Do not include passwords or card details.</div>
        </div>

        {error && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 12 }}>{error}</div>}

        <div style={{ marginTop: 18 }}>
          <button className="btn accent" type="submit" disabled={!ready || submitting}>
            {submitting ? 'Opening…' : 'Open ticket →'}
          </button>
        </div>
      </form>

      <div style={{ marginTop: 32 }}>
        <h3 style={{ fontSize: 14, color: 'var(--fg-mute)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
          Your tickets
        </h3>
        {tickets !== null && tickets.length === 0 && (
          <div className="card" style={{ textAlign: 'center', color: 'var(--fg-mute)' }}>
            No tickets yet.
          </div>
        )}
        {tickets !== null && tickets.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {tickets.map((t) => {
              const ownerLine = t.owner_user_id && t.owner_name
                ? `Handled by ${t.owner_name}${t.owner_role ? `, ${ROLE_LABELS[t.owner_role] ?? t.owner_role}` : ''}.`
                : 'Awaiting triage.';
              return (
                <Link
                  key={t.id}
                  to={`/portal/support/${t.id}`}
                  style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                >
                  <article className="card" style={{ padding: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontSize: 13, color: 'var(--fg-mute)' }}>
                          <span className="rid">{t.id}</span> · {SUPPORT_CATEGORY_LABEL[t.category]}
                          {t.related_ref && <> · ref <span className="rid">{t.related_ref}</span></>}
                        </div>
                        <div style={{ fontWeight: 500, fontSize: 14.5, marginTop: 4 }}>{t.subject}</div>
                      </div>
                      <span className={`chip ${STATUS_TONE[t.status]}`}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
                        {getStatusLabel(t.status, 'client')}
                      </span>
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--fg-mute)', marginTop: 6 }}>
                      {ownerLine}
                    </div>
                    <p style={{ color: 'var(--fg-soft)', fontSize: 13, marginTop: 8, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                      {t.body}
                    </p>
                    <div style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)', marginTop: 8 }}>
                      Opened {t.created_at.slice(0, 10)}
                      {t.resolved_at && ` · resolved ${t.resolved_at.slice(0, 10)}`}
                    </div>
                  </article>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
